/**
 * Create / edit a manual account (FE-3, PHASE1 §4 accounts). On create an
 * optional initial balance is written as a CLBD snapshot; on edit the balance is
 * managed separately via `BalanceDialog`. Currency is EUR-only in Phase 1.
 *
 * The stateful body is mounted only while open, so form state initialises fresh
 * from the current `account` prop each time the dialog opens (no reset effect).
 */
import { useState } from 'react';
import { Modal } from '../../components/ui/Modal.tsx';
import { InlineError } from '../../components/ui/InlineError.tsx';
import { Button } from '../../components/ui/Button.tsx';
import { Field } from '../../components/ui/Field.tsx';
import { Input } from '../../components/ui/Input.tsx';
import { Select, type SelectOption } from '../../components/ui/Select.tsx';
import { ACCOUNT_TYPE } from '../../lib/labels.ts';
import { ApiRequestError } from '../../lib/api.ts';
import { parseAmountInput } from '../../lib/format.ts';
import { useCreateAccount, useUpdateAccount } from '../../lib/hooks/useAccountMutations.ts';
import type { AccountOut, AccountType } from '../../lib/types.ts';

const TYPE_OPTIONS: SelectOption[] = (Object.keys(ACCOUNT_TYPE) as AccountType[]).map((t) => ({
  value: t,
  label: ACCOUNT_TYPE[t],
}));

export interface AccountFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Present → edit mode; absent/null → create mode. */
  account?: AccountOut | null;
}

export function AccountFormDialog({ open, onOpenChange, account }: AccountFormDialogProps) {
  if (!open) return null;
  return <AccountForm account={account ?? null} onClose={() => onOpenChange(false)} />;
}

function AccountForm({ account, onClose }: { account: AccountOut | null; onClose: () => void }) {
  const isEdit = account !== null;
  const create = useCreateAccount();
  const update = useUpdateAccount();

  const [name, setName] = useState(() => account?.name ?? '');
  const [type, setType] = useState<AccountType>(() => account?.type ?? 'checking');
  const [iban, setIban] = useState(() => account?.iban ?? '');
  const [initialBalance, setInitialBalance] = useState('');
  const [error, setError] = useState<string | null>(null);

  const pending = create.isPending || update.isPending;

  function handleSubmit() {
    setError(null);
    const trimmedName = name.trim();
    if (trimmedName === '') {
      setError('Bitte einen Namen angeben.');
      return;
    }
    const trimmedIban = iban.trim();

    let initial: string | null = null;
    if (!isEdit && initialBalance.trim() !== '') {
      initial = parseAmountInput(initialBalance);
      if (initial === null) {
        setError('Startsaldo ist keine gültige Zahl (z. B. -1234,56).');
        return;
      }
    }

    const onError = (err: unknown) => {
      setError(
        err instanceof ApiRequestError
          ? err.message
          : 'Speichern fehlgeschlagen. Bitte erneut versuchen.',
      );
    };

    if (isEdit && account) {
      update.mutate(
        {
          id: account.id,
          body: { name: trimmedName, type, iban: trimmedIban === '' ? null : trimmedIban },
        },
        { onSuccess: onClose, onError },
      );
    } else {
      create.mutate(
        {
          name: trimmedName,
          type,
          iban: trimmedIban === '' ? null : trimmedIban,
          initial_balance: initial,
        },
        { onSuccess: onClose, onError },
      );
    }
  }

  return (
    <Modal
      open
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
      title={isEdit ? 'Konto bearbeiten' : 'Neues Konto'}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={pending}>
            Abbrechen
          </Button>
          <Button onClick={handleSubmit} disabled={pending}>
            {isEdit ? 'Speichern' : 'Konto anlegen'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <InlineError message={error} />

        <Field label="Name" required>
          {(id) => (
            <Input
              id={id}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="z. B. Girokonto Haspa"
              autoFocus
            />
          )}
        </Field>

        <Field label="Kontotyp" required>
          {(id) => (
            <Select
              id={id}
              value={type}
              onValueChange={(v) => setType(v as AccountType)}
              options={TYPE_OPTIONS}
            />
          )}
        </Field>

        <Field label="IBAN" hint="Optional. Wird für den Datei-Import benötigt.">
          {(id) => (
            <Input
              id={id}
              value={iban}
              onChange={(e) => setIban(e.target.value)}
              placeholder="DE00 0000 0000 0000 0000 00"
            />
          )}
        </Field>

        {!isEdit ? (
          <Field
            label="Startsaldo"
            hint="Optional. Negativ = Soll (z. B. Kreditkarte). Wird als CLBD-Snapshot gespeichert."
          >
            {(id) => (
              <Input
                id={id}
                inputMode="decimal"
                value={initialBalance}
                onChange={(e) => setInitialBalance(e.target.value)}
                placeholder="0,00"
              />
            )}
          </Field>
        ) : null}
      </div>
    </Modal>
  );
}
